import { basename, posix, dirname } from '../../../../base/common/path.js';
import { CancellationTokenSource } from '../../../../base/common/cancellation.js';
import { toErrorMessage } from '../../../../base/common/errorMessage.js';
import { parse } from '../../../../base/common/glob.js';
import { joinPath } from '../../../../base/common/resources.js';
import { StopWatch } from '../../../../base/common/stopwatch.js';
import { resolvePatternsForProvider, QueryGlobTester, hasSiblingFn } from './search.js';
class FileSearchEngine {
    constructor(config, provider, sessionToken) {
        this.config = config;
        this.provider = provider;
        this.sessionToken = sessionToken;
        this.isLimitHit = false;
        this.resultCount = 0;
        this.isCanceled = false;
        this.filePattern = config.filePattern;
        this.includePattern = config.includePattern && parse(config.includePattern);
        this.maxResults = config.maxResults || undefined;
        this.exists = config.exists;
        this.activeCancellationTokens = ( new Set());
        this.globalExcludePattern = config.excludePattern && parse(config.excludePattern);
    }
    cancel() {
        this.isCanceled = true;
        this.activeCancellationTokens.forEach(t => t.cancel());
        this.activeCancellationTokens = ( new Set());
    }
    search(_onResult) {
        const folderQueries = this.config.folderQueries || [];
        return ( new Promise((resolve, reject) => {
            const onResult = (match) => {
                this.resultCount++;
                _onResult(match);
            };
            if (this.isCanceled) {
                return resolve({ limitHit: this.isLimitHit });
            }
            if (this.config.extraFileResources) {
                this.config.extraFileResources
                    .forEach(extraFile => {
                    const extraFileStr = ( extraFile.toString());
                    const basename$1 = basename(extraFileStr);
                    if (this.globalExcludePattern && this.globalExcludePattern(extraFileStr, basename$1)) {
                        return;
                    }
                    this.matchFile(onResult, { base: extraFile, basename: basename$1 });
                });
            }
            Promise.all(( folderQueries.map(fq => {
                return this.searchInFolder(fq, onResult);
            }))).then(stats => {
                resolve({
                    limitHit: this.isLimitHit,
                    stats: stats[0] || undefined
                });
            }, (err) => {
                reject(( new Error(toErrorMessage(err))));
            });
        }));
    }
    async searchInFolder(fq, onResult) {
        const cancellation = ( new CancellationTokenSource());
        const options = this.getSearchOptionsForFolder(fq);
        const tree = this.initDirectoryTree();
        const queryTester = ( new QueryGlobTester(this.config, fq));
        const noSiblingsClauses = !queryTester.hasSiblingExcludeClauses();
        let providerSW;
        try {
            this.activeCancellationTokens.add(cancellation);
            providerSW = StopWatch.create();
            const results = await this.provider.provideFileSearchResults({
                pattern: this.config.filePattern || ''
            }, options, cancellation.token);
            const providerTime = providerSW.elapsed();
            const postProcessSW = StopWatch.create();
            if (this.isCanceled && !this.isLimitHit) {
                return null;
            }
            if (results) {
                results.forEach(result => {
                    const relativePath = posix.relative(fq.folder.path, result.path);
                    if (noSiblingsClauses) {
                        const basename$1 = basename(result.path);
                        this.matchFile(onResult, { base: fq.folder, relativePath, basename: basename$1 });
                        return;
                    }
                    this.addDirectoryEntries(tree, fq.folder, relativePath, onResult);
                });
            }
            if (this.isCanceled && !this.isLimitHit) {
                return null;
            }
            this.matchDirectoryTree(tree, queryTester, onResult);
            return {
                providerTime,
                postProcessTime: postProcessSW.elapsed()
            };
        }
        finally {
            cancellation.dispose();
            this.activeCancellationTokens.delete(cancellation);
        }
    }
    getSearchOptionsForFolder(fq) {
        const includes = resolvePatternsForProvider(this.config.includePattern, fq.includePattern);
        const excludes = resolvePatternsForProvider(this.config.excludePattern, fq.excludePattern);
        return {
            folder: fq.folder,
            excludes,
            includes,
            useIgnoreFiles: !fq.disregardIgnoreFiles,
            useGlobalIgnoreFiles: !fq.disregardGlobalIgnoreFiles,
            useParentIgnoreFiles: !fq.disregardParentIgnoreFiles,
            followSymlinks: !fq.ignoreSymlinks,
            maxResults: this.config.maxResults,
            session: this.sessionToken
        };
    }
    initDirectoryTree() {
        const tree = {
            rootEntries: [],
            pathToEntries: Object.create(null)
        };
        tree.pathToEntries['.'] = tree.rootEntries;
        return tree;
    }
    addDirectoryEntries({ pathToEntries }, base, relativeFile, onResult) {
        if (relativeFile === this.filePattern) {
            const basename$1 = basename(this.filePattern);
            this.matchFile(onResult, { base: base, relativePath: this.filePattern, basename: basename$1 });
        }
        function add(relativePath) {
            const basename$1 = basename(relativePath);
            const dirname$1 = dirname(relativePath);
            let entries = pathToEntries[dirname$1];
            if (!entries) {
                entries = pathToEntries[dirname$1] = [];
                add(dirname$1);
            }
            entries.push({
                base,
                relativePath,
                basename: basename$1
            });
        }
        add(relativeFile);
    }
    matchDirectoryTree({ rootEntries, pathToEntries }, queryTester, onResult) {
        const self = this;
        const filePattern = this.filePattern;
        function matchDirectory(entries) {
            const hasSibling = hasSiblingFn(() => ( entries.map(entry => entry.basename)));
            for (let i = 0, n = entries.length; i < n; i++) {
                const entry = entries[i];
                const { relativePath, basename } = entry;
                if (queryTester.matchesExcludesSync(relativePath, basename, filePattern !== basename ? hasSibling : undefined)) {
                    continue;
                }
                const sub = pathToEntries[relativePath];
                if (sub) {
                    matchDirectory(sub);
                }
                else {
                    if (relativePath === filePattern) {
                        continue;
                    }
                    self.matchFile(onResult, entry);
                }
                if (self.isLimitHit) {
                    break;
                }
            }
        }
        matchDirectory(rootEntries);
    }
    matchFile(onResult, candidate) {
        if (!this.includePattern || (candidate.relativePath && this.includePattern(candidate.relativePath, candidate.basename))) {
            if (this.exists || (this.maxResults && this.resultCount >= this.maxResults)) {
                this.isLimitHit = true;
                this.cancel();
            }
            if (!this.isLimitHit) {
                onResult(candidate);
            }
        }
    }
}
class FileSearchManager {
    constructor() {
        this.sessions = ( new Map());
    }
    static { this.BATCH_SIZE = 512; }
    fileSearch(config, provider, onBatch, token) {
        const sessionTokenSource = this.getSessionTokenSource(config.cacheKey);
        const engine = ( new FileSearchEngine(config, provider, sessionTokenSource && sessionTokenSource.token));
        let resultCount = 0;
        const onInternalResult = (batch) => {
            resultCount += batch.length;
            onBatch(( batch.map(m => this.rawMatchToSearchItem(m))));
        };
        return this.doSearch(engine, FileSearchManager.BATCH_SIZE, onInternalResult, token).then(result => {
            return {
                limitHit: result.limitHit,
                stats: {
                    fromCache: false,
                    type: 'fileSearchProvider',
                    resultCount,
                    detailStats: result.stats
                }
            };
        });
    }
    clearCache(cacheKey) {
        const sessionTokenSource = this.getSessionTokenSource(cacheKey);
        sessionTokenSource?.cancel();
    }
    getSessionTokenSource(cacheKey) {
        if (!cacheKey) {
            return undefined;
        }
        if (!( this.sessions.has(cacheKey))) {
            this.sessions.set(cacheKey, ( new CancellationTokenSource()));
        }
        return this.sessions.get(cacheKey);
    }
    rawMatchToSearchItem(match) {
        if (match.relativePath) {
            return {
                resource: joinPath(match.base, match.relativePath)
            };
        }
        else {
            return {
                resource: match.base
            };
        }
    }
    doSearch(engine, batchSize, onResultBatch, token) {
        const listener = token.onCancellationRequested(() => {
            engine.cancel();
        });
        const _onResult = (match) => {
            if (match) {
                batch.push(match);
                if (batchSize > 0 && batch.length >= batchSize) {
                    onResultBatch(batch);
                    batch = [];
                }
            }
        };
        let batch = [];
        return engine.search(_onResult).then(result => {
            if (batch.length) {
                onResultBatch(batch);
            }
            listener.dispose();
            return result;
        }, error => {
            if (batch.length) {
                onResultBatch(batch);
            }
            listener.dispose();
            return Promise.reject(error);
        });
    }
}
export { FileSearchManager };
